// BulkPdfDownloadButtons.jsx — Bulk PDF Download (batch-wise ZIP)
// v2: Fixed blob download, proper streaming, progress feedback
//
// Usage in ProcurementAccountsView toolbar:
//   import BulkPdfDownloadButtons from './BulkPdfDownloadButtons';
//   <BulkPdfDownloadButtons />

import React, { useState, useEffect } from 'react';
import { Button, Tooltip, Dropdown, Tag, message, Badge, Modal } from 'antd';
import {
  CloudDownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  FolderOpenOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const BulkPdfDownloadButtons = () => {
  const [counts, setCounts] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchCounts();
  }, []);

  const fetchCounts = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/procurement/documents/counts`);
      if (res.data.success) {
        setCounts(res.data.counts);
      }
    } catch (err) {
      console.error('Failed to fetch document counts:', err);
    }
  };

  const handleDownload = async (status) => {
    const statusLabel = status === 'all' ? 'all statuses' : status.replace('_', ' ');
    const count = counts?.[status] || 0;

    if (count === 0) {
      message.warning(`No documents found with status: ${statusLabel}`);
      return;
    }

    // Confirm for large downloads
    if (count > 30) {
      Modal.confirm({
        title: `Download ${count} PDFs?`,
        content: (
          <div style={{ fontSize: 13 }}>
            <p>This will download <strong>{count}</strong> invoice PDFs ({statusLabel}) packaged into a ZIP file organized by batch folders.</p>
            <p style={{ color: '#8c8c8c' }}>Large downloads may take 1-2 minutes. The file will save automatically when ready.</p>
          </div>
        ),
        okText: 'Download',
        onOk: () => executeDownload(status, count, statusLabel),
      });
    } else {
      executeDownload(status, count, statusLabel);
    }
  };

  const executeDownload = async (status, count, statusLabel) => {
    setDownloading(status);

    const hideLoading = message.loading(
      `Downloading ${count} ${statusLabel} PDFs into batch folders... Please wait.`,
      0
    );

    try {
      // Use fetch API instead of axios for better streaming/blob support
      const response = await fetch(
        `${API_BASE_URL}/api/procurement/documents/download-all-pdfs?status=${status}`,
        {
          method: 'GET',
          headers: { 'Accept': 'application/zip' },
        }
      );

      if (!response.ok) {
        hideLoading();
        if (response.status === 404) {
          message.warning(`No documents found with status: ${statusLabel}`);
        } else {
          const errorData = await response.json().catch(() => ({}));
          message.error(errorData.message || `Download failed (HTTP ${response.status})`);
        }
        setDownloading(null);
        return;
      }

      // Get the blob
      const blob = await response.blob();
      hideLoading();

      if (blob.size < 100) {
        // Probably an error JSON response
        const text = await blob.text();
        try {
          const errData = JSON.parse(text);
          message.error(errData.message || 'Download failed');
        } catch {
          message.error('Download failed — empty response');
        }
        setDownloading(null);
        return;
      }

      // Get filename from headers
      let filename = `Procurement-PDFs-${status}.zip`;
      const customFilename = response.headers.get('x-zip-filename');
      if (customFilename) filename = customFilename;

      const downloaded = response.headers.get('x-documents-downloaded') || '?';
      const failed = response.headers.get('x-documents-failed') || '0';

      // Create download link and trigger save
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // Cleanup after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 1000);

      const sizeMB = (blob.size / (1024 * 1024)).toFixed(1);

      if (parseInt(failed) > 0) {
        message.warning(
          `Downloaded ${downloaded} PDFs (${failed} failed) — ${sizeMB} MB. Extract ZIP to see batch folders.`,
          6
        );
      } else {
        message.success(
          `${downloaded} PDFs downloaded (${sizeMB} MB). Extract ZIP → batch folders ready.`,
          6
        );
      }
    } catch (err) {
      hideLoading();
      console.error('Bulk PDF download error:', err);
      if (err.name === 'AbortError') {
        message.error('Download timed out. Try downloading fewer files.');
      } else {
        message.error('Download failed. Please try again.');
      }
    } finally {
      setDownloading(null);
    }
  };

  const menuItems = [
    {
      key: 'info',
      label: (
        <div style={{ padding: '4px 0', fontSize: 11, color: '#8c8c8c', borderBottom: '1px solid #f0f0f0', marginBottom: 4 }}>
          <FolderOpenOutlined style={{ marginRight: 6 }} />
          Downloads ZIP with batch folders
        </div>
      ),
      disabled: true,
    },
    {
      key: 'approved',
      icon: downloading === 'approved'
        ? <LoadingOutlined spin style={{ color: '#52c41a' }} />
        : <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, minWidth: 230 }}>
          <span style={{ fontWeight: 500 }}>Approved Invoices</span>
          <Tag color="success" style={{ borderRadius: 4, margin: 0, fontSize: 11 }}>
            {counts?.approved ?? '—'} files
          </Tag>
        </div>
      ),
      disabled: downloading !== null || (counts?.approved || 0) === 0,
    },
    {
      key: 'rejected',
      icon: downloading === 'rejected'
        ? <LoadingOutlined spin style={{ color: '#ff4d4f' }} />
        : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, minWidth: 230 }}>
          <span style={{ fontWeight: 500 }}>Rejected Invoices</span>
          <Tag color="error" style={{ borderRadius: 4, margin: 0, fontSize: 11 }}>
            {counts?.rejected ?? '—'} files
          </Tag>
        </div>
      ),
      disabled: downloading !== null || (counts?.rejected || 0) === 0,
    },
    {
      key: 'pending_review',
      icon: downloading === 'pending_review'
        ? <LoadingOutlined spin style={{ color: '#faad14' }} />
        : <ClockCircleOutlined style={{ color: '#faad14' }} />,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, minWidth: 230 }}>
          <span style={{ fontWeight: 500 }}>Pending Invoices</span>
          <Tag color="warning" style={{ borderRadius: 4, margin: 0, fontSize: 11 }}>
            {counts?.pending_review ?? '—'} files
          </Tag>
        </div>
      ),
      disabled: downloading !== null || (counts?.pending_review || 0) === 0,
    },
    { type: 'divider' },
    {
      key: 'all',
      icon: downloading === 'all'
        ? <LoadingOutlined spin style={{ color: '#1a6ed8' }} />
        : <DownloadOutlined style={{ color: '#1a6ed8' }} />,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, minWidth: 230 }}>
          <span style={{ fontWeight: 600, color: '#1a6ed8' }}>All PDFs (All Statuses)</span>
          <Tag color="blue" style={{ borderRadius: 4, margin: 0, fontSize: 11 }}>
            {counts?.all ?? '—'} files
          </Tag>
        </div>
      ),
      disabled: downloading !== null || (counts?.all || 0) === 0,
    },
  ];

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: ({ key }) => {
          if (key !== 'info') handleDownload(key);
        },
      }}
      trigger={['click']}
      placement="bottomRight"
    >
      <Tooltip title="Download all uploaded invoice PDFs organized by batch folders in a ZIP file">
        <Button
          size="small"
          icon={downloading ? <LoadingOutlined spin /> : <CloudDownloadOutlined />}
          style={{
            borderRadius: 4,
            fontSize: 11,
            color: downloading ? '#faad14' : '#1a6ed8',
            borderColor: downloading ? '#ffe58f' : '#91caff',
          }}
        >
          {downloading ? 'Downloading...' : 'Download PDFs'}
          {!downloading && counts?.all > 0 && (
            <Badge
              count={counts.all}
              size="small"
              style={{ marginLeft: 6, backgroundColor: '#1a6ed8' }}
            />
          )}
        </Button>
      </Tooltip>
    </Dropdown>
  );
};

export default BulkPdfDownloadButtons;